import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@shared/services/api';
import { getLogLevelTone } from '@shared/config/theme';
import { toneChipClasses, toneTextClasses } from '@shared/utils/statusTone';

interface BackupJobLog {
  id: string;
  organization_id: string;
  agent_id: string | null;
  level: string;
  source: string;
  message: string;
  metadata: any;
  created_at: string;
  backup_job_id: string;
}

interface BackupJobLogsViewerProps {
  isOpen: boolean;
  onClose: () => void;
  backupJobId: string;
  backupConfigName: string;
}

export function BackupJobLogsViewer({
  isOpen,
  onClose,
  backupJobId,
  backupConfigName,
}: BackupJobLogsViewerProps) {
  const { t, i18n } = useTranslation('backups');
  const titleId = useId();
  const locale = i18n.resolvedLanguage || i18n.language;
  const [logs, setLogs] = useState<BackupJobLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const previousFocus =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeButtonRef.current?.focus();

    return () => previousFocus?.focus();
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && backupJobId) {
      fetchLogs();
    }
  }, [isOpen, backupJobId]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get<{ logs: BackupJobLog[]; count: number }>(
        `/backups/logs?job_id=${backupJobId}`
      );
      setLogs(response.data.logs || []);
    } catch {
      setError(t('jobs.logViewer.errors.load'));
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
      }).format(date);
    } catch {
      return timestamp;
    }
  };

  // Keep the original message for exports, while localizing known backend
  // events in the table. Unknown messages remain visible verbatim.
  const formatLogMessage = (message: string) => {
    const patterns: Array<[string, RegExp]> = [
      ['dispatchingBackup', /^Dispatching backup '(.+)' to agent$/],
      ['backupDispatched', /^Backup command SENT: '(.+)' dispatched to agent successfully$/],
      ['backupCommandReceived', /^Backup command received from server$/],
      ['backupExecutionStarted', /^Backup execution started: type=(.+)$/],
      ['configurationValidationStarted', /^Starting configuration validation$/],
      ['configurationValidationPassed', /^Configuration validation passed with (\d+) warnings?$/],
      ['s3ConnectionTest', /^Testing S3 connection to bucket=(.+)$/],
      ['fileBackupStarted', /^Starting file backup: paths=(.+)$/],
      ['resticStarted', /^Initializing restic backup process$/],
      [
        'backupSummaryReceived',
        /^Backup summary received: snapshot_id=(\S+) files_added=(\d+) data_added=(\d+)$/,
      ],
      ['fileBackupCompleted', /^File backup completed: snapshot_id=(\S+) duration=([\d.]+)s$/],
      [
        'incrementalBackupAnalysis',
        /^Incremental backup analysis: is_incremental=(\w+) dedup_ratio=([\d.]+)% total_snapshots=(\d+) repo_size=(.+)$/,
      ],
      [
        'retentionPolicyApplying',
        /^Applying retention policy: keep_daily=(\d+) keep_weekly=(\d+) keep_monthly=(\d+)$/,
      ],
      [
        'retentionPolicyApplied',
        /^Retention policy applied: snapshots_removed=(\d+) space_freed=(.+)$/,
      ],
      ['vssSnapshotEnabled', /^VSS snapshot enabled for backup consistency$/],
    ];

    for (const [key, pattern] of patterns) {
      const match = message.match(pattern);
      if (match) {
        const values = match.slice(1);
        if (key === 'backupExecutionStarted' && values[0] === 'files') {
          values[0] = t('jobs.logViewer.messages.fileBackupType');
        }
        if (key === 'incrementalBackupAnalysis') {
          values[0] = t(`jobs.logViewer.messages.${values[0] === 'true' ? 'yes' : 'no'}`);
        }
        return t(`jobs.logViewer.messages.${key}`, {
          value1: values[0],
          value2: values[1],
          value3: values[2],
          value4: values[3],
        });
      }
    }

    return message;
  };

  const handleDownloadLogs = () => {
    // Create log file content
    const logContent = logs
      .map((log) => `[${log.created_at}] [${log.level}] ${log.message}`)
      .join('\n');

    // Create blob and download
    const blob = new Blob([logContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `backup-logs-${backupJobId}.txt`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const handleDialogKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => element.offsetParent !== null);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (focusable.length === 0) {
      event.preventDefault();
      dialogRef.current?.focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[110] overflow-y-auto"
      aria-labelledby={`${titleId}-title`}
      role="dialog"
      aria-modal="true"
      tabIndex={-1}
      ref={dialogRef}
      onKeyDown={handleDialogKeyDown}
    >
      <div className="flex min-h-screen items-center justify-center px-3 py-4 sm:px-4">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
          aria-hidden="true"
          onClick={onClose}
        ></div>

        {/* Center the modal */}
        {/* Modal panel */}
        <div className="relative flex w-full max-w-7xl max-h-[calc(100dvh-2rem)] flex-col overflow-hidden rounded-xl border border-surface-border-light bg-surface-card-light text-left shadow-elevated transition-all dark:border-surface-border-dark dark:bg-surface-card-dark">
          {/* Modal header */}
          <div className="flex shrink-0 flex-col gap-3 border-b border-surface-border-light bg-surface-2-light px-4 py-3 dark:border-surface-border-dark dark:bg-surface-2-dark sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <div>
              <h3
                className="text-lg font-bold leading-6 tracking-tight text-text-primary-light dark:text-text-primary-dark"
                id={`${titleId}-title`}
              >
                {t('jobs.logViewer.title')}
              </h3>
              <p className="text-sm text-text-muted-light dark:text-text-muted-dark mt-1 font-medium">
                {backupConfigName}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="min-h-11 flex-1 rounded-lg bg-brand-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 sm:flex-none"
                onClick={handleDownloadLogs}
                disabled={loading || logs.length === 0}
              >
                {t('jobs.logViewer.download')}
              </button>
              <button
                ref={closeButtonRef}
                type="button"
                aria-label={t('jobs.logViewer.close')}
                title={t('jobs.logViewer.close')}
                className="grid min-h-11 min-w-11 place-items-center rounded-lg bg-surface-card-light text-text-subtle-light transition-colors hover:text-text-primary-light focus-visible:ring-2 focus-visible:ring-brand-500 dark:bg-surface-card-dark dark:text-text-subtle-dark dark:hover:text-text-primary-dark"
                onClick={onClose}
              >
                <span className="sr-only">{t('jobs.logViewer.close')}</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Modal body */}
          <div className="min-h-0 overflow-y-auto bg-surface-card-light px-4 py-5 dark:bg-surface-card-dark sm:p-6">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
                <p className="mt-4 text-text-muted-light dark:text-text-muted-dark font-medium">
                  {t('jobs.logViewer.loading')}
                </p>
              </div>
            ) : error ? (
              <div
                role="alert"
                className="flex flex-col gap-3 rounded-xl border border-state-danger-light/20 bg-state-danger-bg-light p-4 dark:border-state-danger-dark/20 dark:bg-state-danger-bg-dark sm:flex-row sm:items-center sm:justify-between"
              >
                <p className="text-sm font-bold text-state-danger-light dark:text-state-danger-dark">
                  {error}
                </p>
                <button
                  type="button"
                  className="min-h-11 shrink-0 rounded-lg border border-border-strong-light bg-surface-card-light px-4 py-2 text-sm font-semibold text-text-primary-light transition-colors hover:bg-surface-2-light focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-border-strong-dark dark:bg-surface-2-dark dark:text-text-primary-dark dark:hover:bg-surface-border-dark"
                  onClick={fetchLogs}
                  disabled={loading}
                >
                  {t('jobs.logViewer.retry')}
                </button>
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-12">
                <svg
                  className="mx-auto h-12 w-12 text-text-subtle-light dark:text-text-subtle-dark"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <h3 className="mt-2 text-sm font-black text-slate-900 dark:text-white uppercase tracking-wide">
                  {t('jobs.logViewer.emptyTitle')}
                </h3>
                <p className="mt-1 text-sm text-text-muted-light dark:text-text-muted-dark">
                  {t('jobs.logViewer.emptyMessage')}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Log summary */}
                <div className="bg-surface-2-light dark:bg-surface-2-dark rounded-xl p-3 flex justify-between items-center">
                  <span className="text-sm text-slate-600 dark:text-text-subtle-dark">
                    {logs.length === 1
                      ? t('jobs.logViewer.summaryOne', { count: logs.length })
                      : t('jobs.logViewer.summaryOther', { count: logs.length })}
                  </span>
                  <button
                    type="button"
                    className="min-h-11 rounded-lg px-3 text-sm font-semibold text-brand-700 transition-colors hover:bg-surface-card-light hover:text-brand-800 focus-visible:ring-2 focus-visible:ring-brand-500 dark:text-brand-300 dark:hover:bg-surface-card-dark dark:hover:text-brand-200"
                    onClick={fetchLogs}
                    disabled={loading}
                  >
                    {t('jobs.logViewer.refresh')}
                  </button>
                </div>

                {/* Logs table */}
                <div className="max-h-96 overflow-auto rounded-xl border border-surface-border-light bg-surface-card-light dark:border-surface-border-dark dark:bg-surface-2-dark">
                  <table className="w-full table-fixed divide-y divide-surface-border-light dark:divide-surface-border-dark">
                    <thead className="bg-surface-2-light dark:bg-surface-2-dark sticky top-0">
                      <tr>
                        <th className="w-32 px-2 py-2 text-left text-xs font-black uppercase tracking-wider text-text-muted-light dark:text-text-muted-dark sm:w-40 sm:px-4">
                          {t('jobs.logViewer.columns.timestamp')}
                        </th>
                        <th className="w-14 px-2 py-2 text-left text-xs font-black uppercase tracking-wider text-text-muted-light dark:text-text-muted-dark sm:w-20 sm:px-4">
                          {t('jobs.logViewer.columns.level')}
                        </th>
                        <th className="px-2 py-2 text-left text-xs font-black uppercase tracking-wider text-text-muted-light dark:text-text-muted-dark sm:px-4">
                          {t('jobs.logViewer.columns.message')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-surface-card-light dark:bg-surface-card-dark divide-y divide-surface-border-light dark:divide-surface-border-dark">
                      {logs.map((log, index) => {
                        const tone = getLogLevelTone(log.level);
                        // Keep both theme backgrounds when reusing the chip colour for the row.
                        const rowBg = toneChipClasses[tone]
                          .split(' ')
                          .filter((c) => c.startsWith('bg-') || c.startsWith('dark:bg-'))
                          .join(' ');
                        return (
                          <tr key={log.id || index} className={`${toneTextClasses[tone]} ${rowBg}`}>
                            <td className="whitespace-nowrap px-2 py-2 text-xs font-mono text-slate-600 dark:text-text-subtle-dark sm:px-4">
                              {formatTimestamp(log.created_at)}
                            </td>
                            <td className="whitespace-nowrap px-2 py-2 sm:px-4">
                              <span
                                className={`px-2 inline-flex text-xs leading-4 font-black uppercase tracking-wider rounded-full ${toneChipClasses[tone]} ${tone === 'danger' ? 'dark:text-slate-200' : ''}`}
                              >
                                {log.level}
                              </span>
                            </td>
                            <td className="min-w-0 break-words px-2 py-2 text-sm font-mono text-slate-900 dark:text-slate-200 sm:px-4">
                              {formatLogMessage(log.message)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Modal footer */}
          <div className="shrink-0 border-t border-surface-border-light bg-surface-2-light px-4 py-3 dark:border-surface-border-dark dark:bg-surface-2-dark sm:flex sm:flex-row-reverse sm:px-6">
            <button
              type="button"
              className="inline-flex min-h-11 w-full justify-center rounded-lg border border-border-strong-light bg-surface-card-light px-4 py-2 text-sm font-semibold text-text-primary-light shadow-sm transition-colors hover:bg-surface-2-light focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-border-strong-dark dark:bg-surface-2-dark dark:text-text-primary-dark dark:hover:bg-surface-border-dark sm:ml-3 sm:w-auto"
              onClick={onClose}
            >
              {t('jobs.logViewer.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
